import CryptoJS from 'crypto-js';

/**
 * Zero-Knowledge Encryption Utilities
 *
 * This module provides client-side encryption utilities for zero-knowledge architecture:
 * - PBKDF2 key derivation from password
 * - Dual key generation (authKey for authentication, masterKey for encryption)
 * - AES-256-CBC encryption/decryption
 * - All cryptographic operations happen CLIENT-SIDE ONLY
 *
 * CRITICAL: masterKey NEVER leaves the client. Only authHash is sent to server.
 */

// Constants
const PBKDF2_ITERATIONS = 100000; // 100k iterations for security
const KEY_SIZE = 256 / 32; // 256 bits = 8 words (32-bit words in CryptoJS)

/**
 * Generate cryptographically secure random bytes using Web Crypto API
 * Falls back to CryptoJS if window.crypto is not available (should never happen in modern browsers)
 *
 * @param {number} byteCount - Number of random bytes to generate
 * @returns {CryptoJS.lib.WordArray} Random bytes as WordArray
 */
const getSecureRandomBytes = (byteCount) => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    // Use Web Crypto API (cryptographically secure)
    const randomBytes = new Uint8Array(byteCount);
    window.crypto.getRandomValues(randomBytes);

    // Convert Uint8Array to CryptoJS WordArray
    const words = [];
    for (let i = 0; i < randomBytes.length; i += 4) {
      const word = (randomBytes[i] << 24) |
                   (randomBytes[i + 1] << 16) |
                   (randomBytes[i + 2] << 8) |
                   randomBytes[i + 3];
      words.push(word);
    }

    return CryptoJS.lib.WordArray.create(words, byteCount);
  } else {
    // Fallback to CryptoJS (uses Math.random() - not ideal)
    console.warn('window.crypto.getRandomValues not available, falling back to CryptoJS.lib.WordArray.random');
    return CryptoJS.lib.WordArray.random(byteCount);
  }
};

// =============================================================================
// NATIVE WEB CRYPTO PRIMITIVES (fast path)
//
// These use the browser's native crypto.subtle which is 50-100x faster than the
// pure-JS CryptoJS implementation, while producing byte-identical output for the
// same inputs (PBKDF2-HMAC-SHA256 + AES-256-CBC/PKCS7). This keeps the system
// fully zero-knowledge — all derivation/encryption still happens client-side and
// no key material ever leaves the device. Existing CryptoJS-encrypted vault data
// decrypts unchanged (proven by tests/_lib/native-crypto-compat.test.ts).
// =============================================================================

const _subtle = (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle)
  ? globalThis.crypto.subtle
  : null;

const _utf8ToBytes = (str) => new TextEncoder().encode(str);
const _bytesToUtf8 = (bytes) => new TextDecoder().decode(bytes);
// Strict decode: throws on malformed UTF-8, mirroring CryptoJS's
// toString(Utf8) behaviour so a wrong key is reliably rejected rather than
// silently yielding replacement-character garbage.
const _bytesToUtf8Strict = (bytes) => new TextDecoder('utf-8', { fatal: true }).decode(bytes);

const _hexToBytes = (hex) => {
  const len = hex.length / 2;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
};

const _bytesToHex = (bytes) => {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

const _bytesToB64 = (bytes) => {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

const _b64ToBytes = (b64) => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/**
 * Derive a 256-bit key with PBKDF2-HMAC-SHA256 via Web Crypto.
 * @param {Uint8Array} passwordBytes - Raw key material (UTF-8 password or hex-decoded key)
 * @param {Uint8Array} saltBytes
 * @param {number} iterations
 * @returns {Promise<Uint8Array>} 32-byte derived key
 */
const _pbkdf2Bits = async (passwordBytes, saltBytes, iterations) => {
  if (!_subtle) throw new Error('Web Crypto API unavailable');
  const baseKey = await _subtle.importKey('raw', passwordBytes, 'PBKDF2', false, ['deriveBits']);
  const bits = await _subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations, hash: 'SHA-256' },
    baseKey,
    256
  );
  return new Uint8Array(bits);
};

const _aesCbcEncrypt = async (plaintextBytes, keyBytes, ivBytes) => {
  const key = await _subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt']);
  const buf = await _subtle.encrypt({ name: 'AES-CBC', iv: ivBytes }, key, plaintextBytes);
  return new Uint8Array(buf);
};

const _aesCbcDecrypt = async (ciphertextBytes, keyBytes, ivBytes) => {
  const key = await _subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt']);
  const buf = await _subtle.decrypt({ name: 'AES-CBC', iv: ivBytes }, key, ciphertextBytes);
  return new Uint8Array(buf);
};

const _randomBytes = (n) => {
  const out = new Uint8Array(n);
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.getRandomValues) {
    globalThis.crypto.getRandomValues(out);
  } else {
    for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
  }
  return out;
};

// --- High-level async key-derivation helpers (drop-in fast replacements) ---

export const deriveAuthKeyAsync = async (password, email) => {
  if (!_subtle) return deriveAuthKey(password, email);
  if (!password || !email) throw new Error('Password and email are required');
  const bits = await _pbkdf2Bits(_utf8ToBytes(password), _utf8ToBytes(`auth:${email.toLowerCase()}`), PBKDF2_ITERATIONS);
  return _bytesToHex(bits);
};

export const deriveKEKAsync = async (password, email) => {
  if (!_subtle) return deriveKEK(password, email);
  if (!password || !email) throw new Error('Password and email are required');
  const bits = await _pbkdf2Bits(_utf8ToBytes(password), _utf8ToBytes(`kek:${email.toLowerCase()}`), PBKDF2_ITERATIONS);
  return _bytesToHex(bits);
};

export const deriveNomineeMasterKeyAsync = async (password, nomineeEmail) => {
  if (!_subtle) return deriveNomineeMasterKey(password, nomineeEmail);
  if (!password || !nomineeEmail) throw new Error('Password and nominee email are required');
  const bits = await _pbkdf2Bits(_utf8ToBytes(password), _utf8ToBytes(`nominee:${nomineeEmail.toLowerCase()}`), PBKDF2_ITERATIONS);
  return _bytesToHex(bits);
};

export const deriveInvitationKeyAsync = async (invitationKey, email) => {
  if (!_subtle) return deriveInvitationKey(invitationKey, email);
  if (!invitationKey || !email) throw new Error('Invitation key and email are required');
  const bits = await _pbkdf2Bits(_utf8ToBytes(invitationKey), _utf8ToBytes(`invitation:${email.toLowerCase()}`), 10000);
  return _bytesToHex(bits);
};

export const deriveSecurityAnswersKeyAsync = async (answers, email) => {
  if (!_subtle) return deriveSecurityAnswersKey(answers, email);
  if (!answers || answers.length === 0) throw new Error('At least one security answer is required');
  if (!email) throw new Error('Email is required');
  const combined = answers.map(a => a.trim().toLowerCase()).join('|');
  const bits = await _pbkdf2Bits(_utf8ToBytes(combined), _utf8ToBytes(`security:${email.toLowerCase()}`), 50000);
  return _bytesToHex(bits);
};

// --- High-level async encrypt/decrypt (format-compatible with CryptoJS) ---

export const encryptWithMasterKeyAsync = async (plaintext, masterKey) => {
  if (!_subtle) return encryptWithMasterKey(plaintext, masterKey);
  if (!plaintext) throw new Error('Data to encrypt cannot be empty');
  if (!masterKey) throw new Error('Master key is required');
  const iv = _randomBytes(16);
  const salt = _randomBytes(16);
  const key = await _pbkdf2Bits(_hexToBytes(masterKey), salt, 10000);
  const ct = await _aesCbcEncrypt(_utf8ToBytes(plaintext), key, iv);
  return { ciphertext: _bytesToB64(ct), iv: _bytesToB64(iv), salt: _bytesToB64(salt) };
};

export const decryptWithMasterKeyAsync = async (encryptedData, masterKey) => {
  if (!_subtle) return decryptWithMasterKey(encryptedData, masterKey);
  if (!encryptedData || !encryptedData.ciphertext) throw new Error('Invalid encrypted data');
  if (!masterKey) throw new Error('Master key is required');
  const salt = _b64ToBytes(encryptedData.salt);
  const iv = _b64ToBytes(encryptedData.iv);
  const ct = _b64ToBytes(encryptedData.ciphertext);
  const key = await _pbkdf2Bits(_hexToBytes(masterKey), salt, 10000);
  let text;
  try {
    const pt = await _aesCbcDecrypt(ct, key, iv);
    text = _bytesToUtf8Strict(pt);
  } catch (e) {
    throw new Error('Failed to decrypt data');
  }
  if (!text) throw new Error('Decryption failed - invalid key or corrupted data');
  return text;
};

export const encryptJSONAsync = async (data, masterKey) =>
  encryptWithMasterKeyAsync(JSON.stringify(data), masterKey);

export const decryptJSONAsync = async (encryptedData, masterKey) =>
  JSON.parse(await decryptWithMasterKeyAsync(encryptedData, masterKey));

// --- Async DEK wrap/unwrap (KEK is hex-decoded as key material) ---

export const wrapDEKAsync = async (dek, kek) => {
  if (!_subtle) return wrapDEK(dek, kek);
  if (!dek || !kek) throw new Error('DEK and KEK are required');
  const iv = _randomBytes(16);
  const salt = _randomBytes(16);
  const key = await _pbkdf2Bits(_hexToBytes(kek), salt, 10000);
  const ct = await _aesCbcEncrypt(_utf8ToBytes(dek), key, iv);
  return { ciphertext: _bytesToB64(ct), iv: _bytesToB64(iv), salt: _bytesToB64(salt) };
};

export const unwrapDEKAsync = async (wrappedDEK, kek) => {
  if (!_subtle) return unwrapDEK(wrappedDEK, kek);
  if (!wrappedDEK || !wrappedDEK.ciphertext || !kek) throw new Error('Wrapped DEK and KEK are required');
  const salt = _b64ToBytes(wrappedDEK.salt);
  const iv = _b64ToBytes(wrappedDEK.iv);
  const ct = _b64ToBytes(wrappedDEK.ciphertext);
  const key = await _pbkdf2Bits(_hexToBytes(kek), salt, 10000);
  let dek;
  try {
    const pt = await _aesCbcDecrypt(ct, key, iv);
    dek = _bytesToUtf8Strict(pt);
  } catch (e) {
    throw new Error('Failed to unwrap DEK. Invalid password or corrupted data.');
  }
  if (!dek) throw new Error('Unwrapping failed - invalid KEK or corrupted data');
  return dek;
};

/**
 * Derive authentication key from password
 * Used for server authentication only
 *
 * @param {string} password - User password
 * @param {string} email - User email (used as salt component)
 * @returns {string} Hex-encoded authKey (256-bit)
 */
export const deriveAuthKey = (password, email) => {
  if (!password || !email) {
    throw new Error('Password and email are required');
  }

  const salt = `auth:${email.toLowerCase()}`;
  const authKey = CryptoJS.PBKDF2(password, salt, {
    keySize: KEY_SIZE,
    iterations: PBKDF2_ITERATIONS,
    hasher: CryptoJS.algo.SHA256
  });

  return authKey.toString(CryptoJS.enc.Hex);
};

// =============================================================================
// KEY WRAPPING FUNCTIONS (for password-independent data encryption)
// =============================================================================

/**
 * Derive Key Encryption Key (KEK) from password
 * KEK is used to encrypt/decrypt the Data Encryption Key (DEK)
 * This allows password changes without re-encrypting all data
 *
 * @param {string} password - User password
 * @param {string} email - User email (used as salt component)
 * @returns {string} Hex-encoded KEK (256-bit)
 */
export const deriveKEK = (password, email) => {
  if (!password || !email) {
    throw new Error('Password and email are required');
  }

  const salt = `kek:${email.toLowerCase()}`;
  const kek = CryptoJS.PBKDF2(password, salt, {
    keySize: KEY_SIZE,
    iterations: PBKDF2_ITERATIONS,
    hasher: CryptoJS.algo.SHA256
  });

  return kek.toString(CryptoJS.enc.Hex);
};

/**
 * Generate a random Data Encryption Key (DEK)
 * DEK is the actual key used to encrypt all user data
 * It's generated once during signup and wrapped with KEK
 *
 * @returns {string} Hex-encoded random 256-bit DEK
 */
export const generateDEK = () => {
  const randomWords = getSecureRandomBytes(32); // 32 bytes = 256 bits
  return randomWords.toString(CryptoJS.enc.Hex);
};

/**
 * Wrap (encrypt) DEK with KEK
 * The wrapped DEK can be safely stored in the database
 * Only someone with the correct password can unwrap it
 *
 * @param {string} dek - Hex-encoded Data Encryption Key
 * @param {string} kek - Hex-encoded Key Encryption Key
 * @returns {object} { ciphertext, iv, salt } - Wrapped DEK
 */
export const wrapDEK = (dek, kek) => {
  if (!dek || !kek) {
    throw new Error('DEK and KEK are required');
  }

  try {
    // Generate random IV
    const iv = getSecureRandomBytes(16);

    // Generate random salt for additional security
    const salt = getSecureRandomBytes(16);

    // Derive wrapping key from KEK + salt
    const wrappingKey = CryptoJS.PBKDF2(
      CryptoJS.enc.Hex.parse(kek),
      salt,
      {
        keySize: KEY_SIZE,
        iterations: 10000, // Fewer iterations since KEK is already strong
        hasher: CryptoJS.algo.SHA256
      }
    );

    // Encrypt DEK with wrapping key
    const encrypted = CryptoJS.AES.encrypt(dek, wrappingKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return {
      ciphertext: encrypted.ciphertext.toString(CryptoJS.enc.Base64),
      iv: iv.toString(CryptoJS.enc.Base64),
      salt: salt.toString(CryptoJS.enc.Base64)
    };
  } catch (error) {
    throw new Error('Failed to wrap DEK');
  }
};

/**
 * Unwrap (decrypt) wrapped DEK using KEK
 * Recovers the Data Encryption Key from the wrapped form
 *
 * @param {object} wrappedDEK - { ciphertext, iv, salt } (all base64)
 * @param {string} kek - Hex-encoded Key Encryption Key
 * @returns {string} Hex-encoded DEK
 */
export const unwrapDEK = (wrappedDEK, kek) => {
  if (!wrappedDEK || !wrappedDEK.ciphertext || !kek) {
    throw new Error('Wrapped DEK and KEK are required');
  }

  try {
    // Parse wrapped DEK components
    const ciphertext = CryptoJS.enc.Base64.parse(wrappedDEK.ciphertext);
    const iv = CryptoJS.enc.Base64.parse(wrappedDEK.iv);
    const salt = CryptoJS.enc.Base64.parse(wrappedDEK.salt);

    // Derive wrapping key from KEK + salt
    const wrappingKey = CryptoJS.PBKDF2(
      CryptoJS.enc.Hex.parse(kek),
      salt,
      {
        keySize: KEY_SIZE,
        iterations: 10000,
        hasher: CryptoJS.algo.SHA256
      }
    );

    // Decrypt to recover DEK
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      wrappingKey,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    const dek = decrypted.toString(CryptoJS.enc.Utf8);

    if (!dek) {
      throw new Error('Unwrapping failed - invalid KEK or corrupted data');
    }

    return dek;
  } catch (error) {
    throw new Error('Failed to unwrap DEK. Invalid password or corrupted data.');
  }
};

/**
 * Encrypt data using AES-256-CBC with masterKey
 *
 * @param {string} plaintext - Data to encrypt
 * @param {string} masterKey - Hex-encoded master encryption key
 * @returns {object} { ciphertext, iv, salt } - All base64 encoded
 */
export const encryptWithMasterKey = (plaintext, masterKey) => {
  try {
    if (!plaintext) {
      throw new Error('Data to encrypt cannot be empty');
    }
    if (!masterKey) {
      throw new Error('Master key is required');
    }

    // Generate random IV (Initialization Vector)
    const iv = getSecureRandomBytes(16); // 128 bits

    // Generate random salt (for key strengthening)
    const salt = getSecureRandomBytes(16);

    // Derive encryption key from masterKey + salt
    const key = CryptoJS.PBKDF2(
      CryptoJS.enc.Hex.parse(masterKey),
      salt,
      {
        keySize: KEY_SIZE,
        iterations: 10000, // Fewer iterations since masterKey is already strong
        hasher: CryptoJS.algo.SHA256
      }
    );

    // Encrypt using AES-256-CBC
    const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return {
      ciphertext: encrypted.ciphertext.toString(CryptoJS.enc.Base64),
      iv: iv.toString(CryptoJS.enc.Base64),
      salt: salt.toString(CryptoJS.enc.Base64)
    };
  } catch (error) {
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data using AES-256-CBC with masterKey
 *
 * @param {object} encryptedData - { ciphertext, iv, salt } (all base64)
 * @param {string} masterKey - Hex-encoded master encryption key
 * @returns {string} Decrypted plaintext
 */
export const decryptWithMasterKey = (encryptedData, masterKey) => {
  try {
    if (!encryptedData || !encryptedData.ciphertext) {
      throw new Error('Invalid encrypted data');
    }
    if (!masterKey) {
      throw new Error('Master key is required');
    }

    // Parse encrypted data
    const ciphertext = CryptoJS.enc.Base64.parse(encryptedData.ciphertext);
    const iv = CryptoJS.enc.Base64.parse(encryptedData.iv);
    const salt = CryptoJS.enc.Base64.parse(encryptedData.salt);

    // Derive decryption key from masterKey + salt
    const key = CryptoJS.PBKDF2(
      CryptoJS.enc.Hex.parse(masterKey),
      salt,
      {
        keySize: KEY_SIZE,
        iterations: 10000,
        hasher: CryptoJS.algo.SHA256
      }
    );

    // Decrypt
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    const plaintext = decrypted.toString(CryptoJS.enc.Utf8);

    if (!plaintext) {
      throw new Error('Decryption failed - invalid key or corrupted data');
    }

    return plaintext;
  } catch (error) {
    throw new Error('Failed to decrypt data');
  }
};

/**
 * Encrypt JSON object with masterKey
 * Convenience function for encrypting structured data
 *
 * @param {object} data - JSON object to encrypt
 * @param {string} masterKey - Hex-encoded master encryption key
 * @returns {object} { ciphertext, iv, salt }
 */
export const encryptJSON = (data, masterKey) => {
  const plaintext = JSON.stringify(data);
  return encryptWithMasterKey(plaintext, masterKey);
};

/**
 * Decrypt and parse JSON object
 * Convenience function for decrypting structured data
 *
 * @param {object} encryptedData - { ciphertext, iv, salt }
 * @param {string} masterKey - Hex-encoded master encryption key
 * @returns {object} Parsed JSON object
 */
export const decryptJSON = (encryptedData, masterKey) => {
  const plaintext = decryptWithMasterKey(encryptedData, masterKey);
  return JSON.parse(plaintext);
};

// =============================================================================
// NOMINEE-SPECIFIC FUNCTIONS
// =============================================================================

/**
 * Derive nominee master key from password
 * Similar to user masterKey but uses different salt
 *
 * @param {string} password - Nominee password
 * @param {string} nomineeEmail - Nominee email
 * @returns {string} Hex-encoded nomineeMasterKey
 */
export const deriveNomineeMasterKey = (password, nomineeEmail) => {
  if (!password || !nomineeEmail) {
    throw new Error('Password and nominee email are required');
  }

  const salt = `nominee:${nomineeEmail.toLowerCase()}`;
  const nomineeMasterKey = CryptoJS.PBKDF2(password, salt, {
    keySize: KEY_SIZE,
    iterations: PBKDF2_ITERATIONS,
    hasher: CryptoJS.algo.SHA256
  });

  return nomineeMasterKey.toString(CryptoJS.enc.Hex);
};

/**
 * Derive key from invitation key for nominee activation
 * Used to decrypt temp encrypted master key
 *
 * @param {string} invitationKey - 256-bit invitation key
 * @param {string} email - Nominee email
 * @returns {string} Hex-encoded derived key
 */
export const deriveInvitationKey = (invitationKey, email) => {
  if (!invitationKey || !email) {
    throw new Error('Invitation key and email are required');
  }

  const salt = `invitation:${email.toLowerCase()}`;
  const key = CryptoJS.PBKDF2(invitationKey, salt, {
    keySize: KEY_SIZE,
    iterations: 10000, // Fewer iterations - invitation key is already 256 bits
    hasher: CryptoJS.algo.SHA256
  });

  return key.toString(CryptoJS.enc.Hex);
};

/**
 * Hash security question answer
 * Normalized (lowercase, trimmed) before hashing
 *
 * @param {string} answer - Security question answer
 * @returns {string} SHA256 hash
 */
export const hashSecurityAnswer = (answer) => {
  if (!answer) {
    throw new Error('Answer cannot be empty');
  }

  const normalized = answer.trim().toLowerCase();
  return CryptoJS.SHA256(normalized).toString(CryptoJS.enc.Hex);
};

/**
 * Derive encryption key from security question answers
 * Combines multiple answers to create a single encryption key
 * Used for dual-encryption of master keys
 *
 * @param {Array<string>} answers - Array of security question answers (order matters)
 * @param {string} email - Nominee email
 * @returns {string} Hex-encoded derived key
 */
export const deriveSecurityAnswersKey = (answers, email) => {
  if (!answers || answers.length === 0) {
    throw new Error('At least one security answer is required');
  }
  if (!email) {
    throw new Error('Email is required');
  }

  // Normalize all answers and combine them in the original order
  // Security: Order matters - don't sort as it reduces entropy
  const normalizedAnswers = answers.map(a => a.trim().toLowerCase());
  const combinedAnswers = normalizedAnswers.join('|');

  // Use the combined answers as the password and email as salt
  const salt = `security:${email.toLowerCase()}`;
  const key = CryptoJS.PBKDF2(combinedAnswers, salt, {
    keySize: KEY_SIZE,
    iterations: 50000, // Moderate iterations since answers may be simpler
    hasher: CryptoJS.algo.SHA256
  });

  return key.toString(CryptoJS.enc.Hex);
};

/**
 * Generate secure random invitation key
 * 256-bit cryptographically secure random value
 *
 * @returns {string} Hex-encoded 256-bit key
 */
export const generateInvitationKey = () => {
  const randomWords = getSecureRandomBytes(32); // 32 bytes = 256 bits
  return randomWords.toString(CryptoJS.enc.Hex);
};

/**
 * Hash invitation key for storage
 *
 * @param {string} invitationKey - Plain invitation key
 * @returns {string} SHA256 hash
 */
export const hashInvitationKey = (invitationKey) => {
  if (!invitationKey) {
    throw new Error('Invitation key cannot be empty');
  }

  return CryptoJS.SHA256(invitationKey).toString(CryptoJS.enc.Hex);
};

// =============================================================================
// KEY STORAGE (Secure Session Storage)
// =============================================================================

/**
 * Store master key in session storage
 * WARNING: Only store in memory/sessionStorage, NEVER in localStorage
 *
 * @param {string} masterKey - Hex-encoded master key
 */
export const storeMasterKey = (masterKey) => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    // Use sessionStorage (cleared when tab closes)
    window.sessionStorage.setItem('__mk', masterKey);
  }
};

/**
 * Retrieve master key from session storage
 *
 * @returns {string|null} Hex-encoded master key or null if not found
 */
export const retrieveMasterKey = () => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    return window.sessionStorage.getItem('__mk');
  }
  return null;
};

/**
 * Clear master key from session storage
 * Should be called on logout
 */
export const clearMasterKey = () => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem('__mk');
  }
};

// =============================================================================
// PASSWORD STRENGTH VALIDATION
// =============================================================================

/**
 * Check password strength
 *
 * @param {string} password - Password to check
 * @returns {object} { isStrong, score, feedback }
 */
export const checkPasswordStrength = (password) => {
  let score = 0;
  const feedback = [];

  // Length
  if (password.length >= 12) {
    score += 25;
  } else {
    feedback.push('Password should be at least 12 characters');
  }

  // Uppercase
  if (/[A-Z]/.test(password)) {
    score += 25;
  } else {
    feedback.push('Add uppercase letters');
  }

  // Lowercase
  if (/[a-z]/.test(password)) {
    score += 25;
  } else {
    feedback.push('Add lowercase letters');
  }

  // Numbers
  if (/[0-9]/.test(password)) {
    score += 15;
  } else {
    feedback.push('Add numbers');
  }

  // Special characters
  if (/[^A-Za-z0-9]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add special characters');
  }

  return {
    isStrong: score >= 75,
    score,
    feedback,
    level: score >= 90 ? 'strong' : score >= 75 ? 'good' : score >= 50 ? 'fair' : 'weak'
  };
};

/**
 * Generate suggested strong password
 *
 * @returns {string} Generated strong password
 */
export const generateStrongPassword = () => {
  const length = 16;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
  const randomWords = getSecureRandomBytes(length);

  let password = '';
  const bytes = randomWords.words;

  for (let i = 0; i < length; i++) {
    const randomIndex = (bytes[i >> 2] >> ((3 - (i % 4)) * 8)) & 0xff;
    password += charset[randomIndex % charset.length];
  }

  return password;
};

// =============================================================================
// ZERO-KNOWLEDGE FILE DOWNLOAD FUNCTIONS
// =============================================================================

/**
 * Download encrypted payload and extract file locally using ZK encryption
 * Used for client-encrypted documents/policies that need client-side decryption
 *
 * @param {string} encryptedPayload - The encrypted payload from server (encrypted with master key)
 * @param {number} fileIndex - Index of file to extract
 * @param {string} defaultFilename - Fallback filename if not specified in metadata
 * @returns {Promise<void>} Triggers browser download
 */
export const downloadEncryptedFile = async (
  encryptedPayload,
  fileIndex,
  defaultFilename
) => {
  try {
    if (!encryptedPayload) {
      throw new Error('Encrypted payload is required');
    }

    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      throw new Error('Master key not found. Please log in again.');
    }

    // Decrypt the payload with master key (ZK encryption) via the native fast
    // path — file payloads are large (base64), so the sync path froze the UI.
    const decryptedData = await decryptJSONAsync(encryptedPayload, masterKey);

    // Extract file from decrypted data (could be in 'files' or 'documents' key)
    const files = decryptedData.files || decryptedData.documents || [];

    if (fileIndex >= files.length) {
      throw new Error(`File index ${fileIndex} out of range`);
    }

    // Handle both old format (string) and new format (object with name and data)
    let fileData;
    let filename;

    const fileObj = files[fileIndex];
    if (typeof fileObj === 'string') {
      fileData = fileObj;
      filename = defaultFilename;
    } else {
      fileData = fileObj.data;
      filename = fileObj.name || defaultFilename;
    }

    if (!fileData) {
      throw new Error('No file data in document');
    }

    // Strip data URL prefix if present
    let base64Data = fileData;
    if (base64Data.includes(',') && base64Data.startsWith('data:')) {
      base64Data = base64Data.split(',')[1];
    }

    // Convert base64 to blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: 'application/octet-stream' });

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  } catch (error) {
    throw error;
  }
};

/**
 * Download encrypted payload and extract attachment locally using ZK encryption
 * Used for client-encrypted records that need client-side decryption
 *
 * @param {string} encryptedPayload - The encrypted payload from server (encrypted with master key)
 * @param {number} attachmentIndex - Index of attachment to extract
 * @param {string} defaultFilename - Fallback filename if not specified in metadata
 * @returns {Promise<void>} Triggers browser download
 */
export const downloadEncryptedAttachment = async (
  encryptedPayload,
  attachmentIndex,
  defaultFilename
) => {
  try {
    if (!encryptedPayload) {
      throw new Error('Encrypted payload is required');
    }

    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      throw new Error('Master key not found. Please log in again.');
    }

    // Decrypt the payload with master key (ZK encryption) via the native fast path.
    const decryptedData = await decryptJSONAsync(encryptedPayload, masterKey);

    // Extract attachment from decrypted data
    const attachments = decryptedData.attachments || [];

    if (attachmentIndex >= attachments.length) {
      throw new Error(`Attachment index ${attachmentIndex} out of range`);
    }

    const attachment = attachments[attachmentIndex];
    const fileData = attachment.data;
    const filename = attachment.name || defaultFilename;

    if (!fileData) {
      throw new Error('No file data in attachment');
    }

    // Strip data URL prefix if present
    let base64Data = fileData;
    if (base64Data.includes(',') && base64Data.startsWith('data:')) {
      base64Data = base64Data.split(',')[1];
    }

    // Convert base64 to blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: 'application/octet-stream' });

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  } catch (error) {
    throw error;
  }
};

/**
 * Smart download handler for files from documents/policies using ZK encryption
 * Tries regular endpoint first, falls back to encrypted endpoint if needed
 *
 * @param {string} endpoint - API endpoint path (e.g., '/documents/docId/download/0')
 * @param {number} fileIndex - Index of file
 * @param {string} defaultFilename - Fallback filename
 * @param {string} token - Auth token
 * @returns {Promise<void>} Triggers browser download
 */
export const handleSmartFileDownload = async (
  endpoint,
  fileIndex,
  defaultFilename,
  token
) => {
  try {
    // Try regular download first
    let response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.status === 400) {
      // Likely client-encrypted, try encrypted endpoint
      const encryptedEndpoint = `${endpoint}/encrypted`;
      response = await fetch(encryptedEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const metadata = await response.json();

      // Use zero-knowledge decryption
      await downloadEncryptedFile(
        metadata.encryptedPayload,
        fileIndex,
        defaultFilename
      );
    } else if (response.ok) {
      // Regular download succeeded
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } else {
      throw new Error(`Download failed: ${response.statusText}`);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Smart download handler for attachments using ZK encryption
 * Tries regular endpoint first, falls back to encrypted endpoint if needed
 *
 * @param {string} endpoint - API endpoint path (e.g., '/financial/recordId/download/0')
 * @param {number} attachmentIndex - Index of attachment
 * @param {string} defaultFilename - Fallback filename
 * @param {string} token - Auth token
 * @returns {Promise<void>} Triggers browser download
 */
export const handleSmartAttachmentDownload = async (
  endpoint,
  attachmentIndex,
  defaultFilename,
  token
) => {
  try {
    // Try regular download first
    let response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.status === 400) {
      // Likely client-encrypted, try encrypted endpoint
      const encryptedEndpoint = `${endpoint}/encrypted`;
      response = await fetch(encryptedEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const metadata = await response.json();

      // Use zero-knowledge decryption
      await downloadEncryptedAttachment(
        metadata.encryptedPayload,
        attachmentIndex,
        defaultFilename
      );
    } else if (response.ok) {
      // Regular download succeeded
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } else {
      throw new Error(`Download failed: ${response.statusText}`);
    }
  } catch (error) {
    throw error;
  }
};

// Export all functions
export default {
  deriveAuthKey,
  deriveKEK,
  generateDEK,
  wrapDEK,
  unwrapDEK,
  encryptWithMasterKey,
  decryptWithMasterKey,
  encryptJSON,
  decryptJSON,
  deriveNomineeMasterKey,
  deriveInvitationKey,
  hashSecurityAnswer,
  generateInvitationKey,
  hashInvitationKey,
  // Native Web Crypto fast path (async)
  deriveAuthKeyAsync,
  deriveKEKAsync,
  deriveNomineeMasterKeyAsync,
  deriveInvitationKeyAsync,
  deriveSecurityAnswersKeyAsync,
  encryptWithMasterKeyAsync,
  decryptWithMasterKeyAsync,
  encryptJSONAsync,
  decryptJSONAsync,
  wrapDEKAsync,
  unwrapDEKAsync,
  storeMasterKey,
  retrieveMasterKey,
  clearMasterKey,
  checkPasswordStrength,
  generateStrongPassword,
  handleSmartFileDownload,
  handleSmartAttachmentDownload,
  downloadEncryptedFile,
  downloadEncryptedAttachment
};
